import { ChangeEvent, ReactElement, useContext, useState } from 'react';
import { Box, Stack } from '@chakra-ui/react';

import {
  DocumentFormBlock,
  DocumentFormBlockAction,
  DocumentFormField,
  Nullable,
  TenantIconsMap,
} from '~/core/shared/types';
import { DefinitionContext } from '../../contexts';
import { useMiddleOfficeConfig } from '../../contexts/middleOfficeConfig';
import { DocumentFormContext } from '../application';
import { blockMatchesSearch } from './logic/blockMatchesSearch';
import { fieldMatchesSearch } from './logic/fieldMatchesSearch';
import { ActionHandler, Sizes } from './ActionHandler.component';
import { SearchInput } from './CrudSearch.component';
import { SidebarBuilder } from './SidebarBuilder.component';

type Meta = {
  title: string;
  subtitle: string;
  icon: string;
  [key: string]: any;
};

type ContentMeta = {
  title: string;
  count: number;
  icon: string;
  action: string[];
  [key: string]: any;
};

type ContentItem = {
  meta: ContentMeta;
  fields: any[];
  blocks: ContentMeta[];
  [key: string]: any;
};

type DocumentData = {
  meta?: Meta;
  content?: any[];
  [key: string]: any;
};

const searchDocumentData = (
  documentData: DocumentData,
  searchTerm: string,
): any => {
  if (!searchTerm) return documentData;

  const searchInContentMeta = (contentMeta: ContentMeta): boolean =>
    Object.values(contentMeta).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm),
    );

  const searchInFields = (fields: DocumentFormField[]): boolean =>
    fields.some((field) => fieldMatchesSearch(field, searchTerm));

  const searchInBlocks = (blocks: DocumentFormBlock[]): boolean =>
    blocks.some(
      (block) =>
        blockMatchesSearch(block, searchTerm) ||
        searchInFields(block.fields) ||
        searchInBlocks(block.blocks),
    );

  const filteredContent = documentData?.filter((contentItem: any) => {
    const { meta, fields, blocks } = contentItem;
    return (
      searchInContentMeta(meta) ||
      searchInFields(fields) ||
      searchInBlocks(blocks)
    );
  });

  return filteredContent;
};

export const SidebarPanel = (): ReactElement => {
  const {
    form: documentData,
    setSelectedBlock,
    selectedBlock,
  } = useContext(DocumentFormContext);
  const { definition } = useContext(DefinitionContext);
  const blockData = documentData?.content;
  const [filteredBlockData, setFilteredBlockData] =
    useState<DocumentFormBlock[]>(blockData);
  const middleOfficeConfig = useMiddleOfficeConfig();
  const icons: Nullable<TenantIconsMap> =
    middleOfficeConfig.getIcons(definition) || {};

  const handleInputChange = (event: ChangeEvent<{ value: string }>): void => {
    const userInput = event.target.value;
    const filtered = searchDocumentData(blockData, userInput.toLowerCase());
    setFilteredBlockData(filtered);
  };

  return (
    <Stack
      bg="white"
      border="solid 1px"
      borderColor="gray.200"
      h="calc(75vh)"
      maxW="325px"
      overflowY="auto"
      position="fixed"
      w="full"
    >
      <Box pb={1} pt={3}>
        <SearchInput handleInputChange={handleInputChange} />
      </Box>
      <SidebarBuilder
        blockData={filteredBlockData || blockData}
        icons={icons}
        selected={selectedBlock}
        setSelected={(block) => setSelectedBlock(block)}
      />
    </Stack>
  );
};

export const DisplayActions = (
  actions: DocumentFormBlockAction[],
  block: DocumentFormBlock,
  fullButton?: boolean,
  size?: Sizes,
): ReactElement[] =>
  actions?.map((item, i) => (
    <ActionHandler
      // eslint-disable-next-line react/no-array-index-key
      key={i}
      action={item}
      block={block}
      fullButton={fullButton}
      path={block.meta.path}
      size={size}
    />
  ));
